import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { UsersThree, Package, FileText } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { ManagerCard } from "../components/admin/ManagerCard";
import { ManagerLeaderboard } from "../components/admin/ManagerLeaderboard";
import { LeadAssignmentPanel } from "../components/admin/LeadAssignmentPanel";
import { AffiliateRosterSection } from "../components/admin/AffiliateRosterSection";
import { AffiliateFundingCommissionsTab } from "../components/admin/AffiliateFundingCommissionsTab";
import { PendingSignupsPanel } from "../components/admin/PendingSignupsPanel";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const navItems = [{ to: "/admin/management-team", label: "Management Team", icon: <UsersThree size={18} weight="duotone" />, testId: "nav-admin-management-team" }];

export default function AdminManagementTeamPage() {
  const confirmEdit = useEditConfirm();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementDraft, setAgreementDraft] = useState("");
  const [agreementVersion, setAgreementVersion] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus");

  const fetchManagers = async () => {
    try {
      const { data } = await api.get("/admin/management-team");
      setManagers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load the Management Team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchManagers(); }, []);

  useEffect(() => {
    if (!focusId || managers.length === 0) return;
    const el = document.getElementById(`manager-card-${focusId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, managers]);

  const openAgreementEditor = async () => {
    try {
      const { data } = await api.get("/admin/agreement/current");
      setAgreementDraft(data.text);
      setAgreementVersion(data.version);
      setAgreementOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const publishAgreement = async () => {
    if (!(await confirmEdit("the Manager Partner Agreement (publish new version)"))) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/agreement/new-version", { text: agreementDraft });
      toast.success(`Published Manager Partner Agreement version ${data.version} — reps get it automatically`);
      setAgreementOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  const managerOptions = managers.map((m) => ({ id: m.id, code: m.code, display_name: m.display_name }));

  return (
    <DashboardLayout navItems={navItems} moduleName="Management Team" moduleBackPath="/admin">
      <div data-testid="admin-management-team-page">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight" title="Every manager, their reps, commissions, and payment info, plus lead assignment and the affiliate roster.">Management Team</h1>
          <button data-testid="admin-edit-agreement-button" onClick={openAgreementEditor} title="Edit the Manager Partner Agreement text. Publishing a new version is automatically sent to any manager who hasn't signed yet." className="flex items-center gap-1.5 text-xs text-igc-muted hover:text-igc-goldText transition-colors border border-igc-gold/30 px-3 py-2">
            <FileText size={14} weight="duotone" /> Manage Partner Agreement
          </button>
        </div>
        <p className="text-igc-muted text-sm mb-8">Managers, their referral links, commissions, reps, and payment info — plus lead assignment, affiliate funding commissions, and the full sales rep roster.</p>

        <PendingSignupsPanel />

        <Tabs defaultValue="overview">
          <TabsList className="bg-igc-surface border border-igc-gold/15 mb-6">
            <TabsTrigger data-testid="management-team-tab-overview" value="overview" className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Overview</TabsTrigger>
            <TabsTrigger data-testid="management-team-tab-funding-commissions" value="funding-commissions" className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Affiliate Funding Commissions</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-8 flex items-center justify-between flex-wrap gap-3" data-testid="lead-package-coming-soon-card">
              <div>
                <p className="font-serif text-lg text-igc-cream">Lead Package Sales System</p>
                <p className="text-igc-muted text-sm mt-1">Foundation is live — bundle and price leads for future sale to managers and affiliates.</p>
              </div>
              <Link to="/admin/lead-packages" data-testid="lead-package-manage-link" title="Opens the Lead Packages page to create, price, and fulfill lead bundles." className="flex items-center gap-1.5 text-igc-goldText text-[11px] font-medium uppercase tracking-widest border border-igc-gold/30 px-3 py-1.5 hover:bg-igc-gold/10 transition-colors">
                <Package size={13} /> Manage Packages
              </Link>
            </div>

            <LeadAssignmentPanel managers={managerOptions} onAssigned={fetchManagers} />

            <ManagerLeaderboard managers={managers} />

            <h2 className="font-serif text-2xl text-igc-cream tracking-tight mb-4" title="Every manager in the system with their commission and team stats.">Managers</h2>
            {!loading && managers.length === 0 && <p className="text-igc-muted text-sm mb-10" data-testid="management-team-empty-state">No managers found.</p>}
            {loading ? (
              <p className="text-igc-muted text-sm mb-10" data-testid="management-team-loading">Loading managers...</p>
            ) : (
              <div className="space-y-3 mb-12">
                {managers.map((m) => (
                  <ManagerCard key={m.id} manager={m} onRateUpdated={fetchManagers} highlighted={m.id === focusId} />
                ))}
              </div>
            )}

            <AffiliateRosterSection />
          </TabsContent>

          <TabsContent value="funding-commissions">
            <AffiliateFundingCommissionsTab managers={managerOptions} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={agreementOpen} onOpenChange={setAgreementOpen}>
        <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="admin-agreement-editor-modal">
          <DialogHeader>
            <DialogTitle className="font-serif" title="Editing publishes a new version to any manager who hasn't signed yet.">Manager Partner Agreement {agreementVersion ? `— current version ${agreementVersion}` : ""}</DialogTitle>
          </DialogHeader>
          <p className="text-igc-muted text-sm">Editing publishes a new version. Managers who already signed keep their signed copy on record; anyone who hasn't signed yet automatically sees this new text.</p>
          <Textarea data-testid="admin-agreement-editor-textarea" value={agreementDraft} onChange={(e) => setAgreementDraft(e.target.value)} title="Edit the full agreement text here." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream min-h-[300px] font-mono text-xs" />
          <Button data-testid="admin-agreement-publish-button" onClick={publishAgreement} disabled={publishing} title="Publishes this text as the new active version of the agreement." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
            {publishing ? "Publishing..." : "Publish New Version"}
          </Button>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
